import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { CommunityId, daysAgo, formatCurrency } from "@sp-crm/core";
import { Icon } from "components/icon";
import { defaultLinkStyle } from "components/ui/link";
import { FindOrCreateCommunityInsightsMutation } from "generated/graphql";
import React from "react";
import { CommunityPricingMissing } from "./community-pricing-missing";

interface CommunityInsightsPricingProps {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
    communityId: CommunityId;
}

export const CommunityInsightsPricing = (props: CommunityInsightsPricingProps) => {
    const { listing, communityId } = props;

    const formatPriceRange = () => {
        if (!listing.pricing) return null;
        const { minPrice, maxPrice } = listing.pricing;
        if (minPrice && maxPrice) {
            return `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)} per month`;
        }
        if (minPrice) {
            return `Starting at ${formatCurrency(minPrice)} per month`;
        }
        if (maxPrice) {
            return `Up to ${formatCurrency(maxPrice)} per month`;
        }
        return null;
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <div className="w-7">
                        <Icon name="17_Budget" />
                    </div>
                    <span className="text-lg text-gray-700">Pricing</span>
                </div>
            </div>
            {!listing.pricing ? (
                <CommunityPricingMissing listing={listing} communityId={communityId} />
            ) : (
                <div className="space-y-4 mt-2">
                    {formatPriceRange() ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Price Range</p>
                            <p className="text-gray-600">{formatPriceRange()}</p>
                        </div>
                    ) : null}
                    {listing.pricing.notes ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Notes</p>
                            <p className="text-gray-600">{listing.pricing.notes}</p>
                        </div>
                    ) : null}
                    {listing.pricing?.files?.length > 0 ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Files</p>
                            <ul className="space-y-2">
                                {listing.pricing.files.map(file => (
                                    <li key={file.id}>
                                        <a
                                            href={file.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${defaultLinkStyle} text-sm flex items-center space-x-1`}>
                                            <ArrowDownTrayIcon className="w-4 h-4 flex-shrink-0" />
                                            <span>{file.name}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    <div>
                        <p className="text-gray-500 text-sm">
                            Last updated {daysAgo(listing.pricing.lastUpdatedAt)}.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};
